package dev.lingvo.shared.agent

/**
 * Everything an agent may ask Lingvo to do, declared once.
 *
 * **This is data, in `:shared`, on purpose.** It compiles for JS as well as the JVM, so the web app
 * can render the exact inventory a connection will see — which no surface could do while each
 * server hand-built its own manifest. It performs no I/O and reaches no database; each transport
 * binds its own handlers and may *filter* this list by scope, role or `--read-only`, but may not
 * rename, re-describe or re-schema an entry.
 *
 * The binding tests in `:server` and `:cli` fail the build when a transport serves a tool this list
 * does not declare, or declares a transport it does not bind. That is the mechanism that makes "one
 * catalogue" true rather than aspirational: nothing failed, before, when a tool was added to one
 * server and not the other.
 *
 * **A tool arrives here in the same change that binds a handler for it.** The spec's full surface is
 * larger than this list; the rest lands with the phases that build it, because a catalogue that
 * declares tools nothing implements is a manifest an agent calls and is refused by.
 *
 * @see AgentToolSpec for why declaration and handler are separate.
 */
object AgentCatalogue {

    val TOOLS: List<AgentToolSpec> = AgentReadTools.TOOLS + AgentWriteTools.TOOLS + SourceAwareTools.TOOLS + ScreenshotTools.TOOLS

    /** The tools one transport actually carries, in declaration order. */
    fun forTransport(transport: Transport): List<AgentToolSpec> =
        TOOLS.filter { transport in it.transports }

    fun byName(name: String): AgentToolSpec? = TOOLS.firstOrNull { it.name == name }
}

/**
 * The argument every project-scoped tool takes, described once.
 *
 * **Optional on both transports.** Requiring it remotely and defaulting it locally was one of the
 * six divergences that started this: an agent that learned the schema from one server constructed
 * invalid calls against the other. Each transport resolves the default its own way — `lingvo.yaml`
 * locally, the connection's single project remotely — and that is a handler's business, not a
 * schema's.
 */
internal val PROJECT_ARGUMENT = AgentSchema.string(
    "Project slug. Optional: the local server falls back to lingvo.yaml, and the remote server to " +
        "this connection's project when it has exactly one.",
)
